import { useState, useEffect } from 'react';

interface WallAccessState {
  isAllowed: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to check if wall-screen access is allowed based on IP whitelist.
 * Only makes the API call when enabled is true.
 */
export function useWallAccess(enabled: boolean): WallAccessState {
  const [state, setState] = useState<WallAccessState>({
    isAllowed: false,
    isLoading: enabled, // Start loading if enabled
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ isAllowed: false, isLoading: false, error: null });
      return;
    }

    let cancelled = false;

    const checkAccess = async () => {
      try {
        const response = await fetch('/api/auth/wall-access');
        
        if (!response.ok) {
          throw new Error('Failed to check wall access');
        }

        const data = await response.json();
        
        // Wall access response logged for debugging only in development
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[useWallAccess] Response:', {
            wallAccessAllowed: data.wallAccessAllowed,
            whitelistEnabled: data.whitelistEnabled,
            detectedIp: data.detectedIp,
          });
        }
        
        if (!cancelled) {
          setState({
            isAllowed: data.wallAccessAllowed === true,
            isLoading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            isAllowed: false,
            isLoading: false,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
}
